import { Link } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import instaIcon from "../../assets/Icons/insta-icon.png";
import comingSoonLottie from "../../assets/lottie-files/comingSoonEvent.lottie";
import EventsGrid from "./EventsGrid";
import ROUTES from "../../routes";
import { EXTERNAL_LINKS } from "../../constants/externalLinks";
import { motion } from "framer-motion";

const noEventsState = (
  <div style={{ textAlign: "center", padding: "2.5rem 1rem 1rem" }}>
    <DotLottieReact
      src={comingSoonLottie}
      loop
      autoplay
      style={{ width: "min(260px, 70vw)", height: "auto", margin: "0 auto" }}
    />
    <h3
      style={{
        color: "#1a4209",
        fontWeight: 800,
        marginTop: "1rem",
        fontSize: "clamp(1.1rem, 3vw, 1.5rem)",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      Stay Tuned — Events Coming Soon!
    </h3>
    <p
      style={{
        color: "#777",
        maxWidth: "360px",
        margin: "0.6rem auto 1.8rem",
        lineHeight: 1.6,
        fontSize: "0.95rem",
      }}
    >
      We're planning our next exciting market events. Follow us on social media
      to be the first to know!
    </p>
    <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
      <a
        href={EXTERNAL_LINKS.instagram}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          background: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366)",
          color: "#fff",
          padding: "0.65em 1.4em",
          borderRadius: "999px",
          fontWeight: 700,
          textDecoration: "none",
          fontSize: "0.9rem",
          boxShadow: "0 4px 16px rgba(220,39,67,0.3)",
        }}
      >
        <img src={instaIcon} alt="" width={16} height={16} style={{ filter: "brightness(10)" }} />
        Follow on Instagram
      </a>
      <Link
        to={ROUTES.SEE_EVENTS}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          background: "#1a4209",
          color: "#fff",
          padding: "0.65em 1.4em",
          borderRadius: "999px",
          fontWeight: 700,
          textDecoration: "none",
          fontSize: "0.9rem",
          boxShadow: "0 4px 16px rgba(26,66,9,0.2)",
        }}
      >
        View Past Events
      </Link>
    </div>
  </div>
);

export default function UpcomingEvents() {
  return (
    <section
      style={{
        background: "linear-gradient(180deg, #fffdf8 0%, #fff8ee 50%, #fffdf8 100%)",
        padding: "clamp(56px, 9vw, 100px) 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative blobs */}
      <div className="ue-blob ue-blob-1" />
      <div className="ue-blob ue-blob-2" />

      <div
        style={{
          maxWidth: "1160px",
          margin: "0 auto",
          padding: "0 clamp(16px, 4vw, 32px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: "clamp(32px, 5vw, 52px)" }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255,140,0,0.1)",
              border: "1px solid rgba(255,140,0,0.25)",
              borderRadius: "999px",
              padding: "9px 22px",
              color: "#e07000",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "2px",
              textTransform: "uppercase",
              marginBottom: "16px",
            }}
          >
            🎉 What's On
          </span>
          <h2
            style={{
              fontSize: "clamp(2rem, 6vw, 3.8rem)",
              fontWeight: 900,
              color: "#1a4209",
              fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
              letterSpacing: "-1.5px",
              margin: "0 0 12px",
            }}
          >
            Upcoming{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #6dc744, #4a8c25)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Events
            </span>
          </h2>
          <p
            style={{
              color: "#777",
              fontSize: "clamp(0.92rem, 2vw, 1.1rem)",
              maxWidth: "480px",
              margin: "0 auto",
              lineHeight: 1.65,
            }}
          >
            Don't miss what's happening at your favourite Saturday market.
          </p>
        </motion.div>

        {/* Events grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.65 }}
        >
          <EventsGrid limit={3} upcomingOnly emptyState={noEventsState} />
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ textAlign: "center", marginTop: "clamp(24px, 4vw, 40px)" }}
        >
          <Link
            to={ROUTES.SEE_EVENTS}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "linear-gradient(90deg, #1a4209, #2d6a14)",
              color: "#fff",
              padding: "0.9em 2.6em",
              borderRadius: "999px",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 24px rgba(26,66,9,0.25)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            className="ue-cta-btn"
          >
            Explore All Events
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </motion.div>
      </div>

      <style>{`
        .ue-blob {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(80px);
        }
        .ue-blob-1 {
          width: 420px; height: 420px;
          background: rgba(255,165,0,0.07);
          top: -100px; right: -100px;
        }
        .ue-blob-2 {
          width: 340px; height: 340px;
          background: rgba(109,199,68,0.08);
          bottom: -80px; left: -80px;
        }
        .ue-cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(26,66,9,0.35) !important;
        }
      `}</style>
    </section>
  );
}
